// slideTransitions.js - slide transitions, one definition for the preview and the export.
//
// Every transition is two animations:
//   part 1 ("..._1.json") covers the screen and ends fully covered,
//   part 2 ("..._2.json") starts fully covered and reveals the next slide.
// For a seamless change, part 1 must END exactly at the slide change and part 2 START exactly
// there, each lasting as long as its animation really is.
//
// Before, Editor.Component.jsx had 18 hand-copied blocks for the preview and 18 for the export,
// with fixed durations that did not match the animations (e.g. Arrow_Move part 1 is 2.5 s, the code
// said 1.8 s), a start that did not match the duration (3_Panes), and in the export both parts were
// stretched to 1.5x: part 1 then stayed on top of the reveal for up to a second, so the next slide
// popped in at the end instead of being revealed.

import { loadLottieJson } from "./lottieCache";

// name -> [part 1 file, part 2 file, part 1 length, part 2 length] (seconds).
// The lengths are the measured lengths of the 16:9 files (for the eight transitions whose files
// were not available to measure: the old fixed values). The real lengths are read from the files
// in the background (preloadSlideTransitions) and replace these as soon as they are known.
export const SLIDE_TRANSITIONS = {
  Arrow_Move: ["Transition1_1.json", "Transition1_2.json", 2.5, 1.5],
  "3_Panes": ["Transition22_1.json", "Transition22_2.json", 1, 1],
  Arrow_Shuffle: ["Transition3_1.json", "Transition3_2.json", 2, 1.333],
  Square_Pegs: ["Transition6_1.json", "Transition6_2.json", 2.167, 1.833],
  Line_Swipe: ["Transition5_1.json", "Transition5_2.json", 1.667, 2.317],
  Square_Slice: ["Transition4_1.json", "Transition4_2.json", 1.75, 3.167],
  Spirals: ["Transition26_1.json", "Transition26_2.json", 1.75, 1.483],
  Tricut: ["Transition11_1.json", "Transition11_2.json", 1.5, 2.0],
  Hor_Drop: ["Transition12_1.json", "Transition12_2.json", 1.5, 2.0],
  Side_Wave: ["Transition13_1.json", "Transition13_2.json", 2.0, 3.0],
  Slide_Down: ["Transition14_1.json", "Transition14_2.json", 2.0, 3.0],
  Mash_Circle: ["Transition15_1.json", "Transition15_2.json", 1.0, 1.0],
  Bars: ["Transition16_1.json", "Transition16_2.json", 1.0, 1.0],
  Wipe_Top: ["Transition17_1.json", "Transition17_2.json", 0.8, 1.0],
  Diagonal_Rays: ["Transition18_1.json", "Transition18_2.json", 0.5, 1.0],
  Fading_Shapes: ["Transition24_1.json", "Transition24_2.json", 2, 2.25],
  Circle_Tunnel: ["Transition21_1.json", "Transition21_2.json", 2, 1.75],
  Left_Swipe: ["Transition27_1.json", "Transition27_2.json", 1.75, 1],
};

/** Folder of the transition files for the project's canvas size. */
export function slideTransitionFolder(canvasSize) {
  if (canvasSize === "1:1") return "/Assets/SlideTransitions/square/";
  if (canvasSize === "9:16") return "/Assets/SlideTransitions/potrait/";
  return "/Assets/SlideTransitions/";
}

/** Real length of an animation in seconds, or null. */
function animationSeconds(json) {
  if (!json || !(json.fr > 0)) return null;
  const frames = Number(json.op) - Number(json.ip || 0);
  return frames > 0 ? frames / json.fr : null;
}

/** "00:00:01:750" from seconds (the editor's time format). */
export function secondsToTimeString(seconds) {
  const totalMs = Math.max(0, Math.round(seconds * 1000));
  const pad = (n, z = 2) => String(n).padStart(z, "0");
  const ms = totalMs % 1000;
  const s = Math.floor(totalMs / 1000) % 60;
  const m = Math.floor(totalMs / 60000) % 60;
  const h = Math.floor(totalMs / 3600000);
  return `${pad(h)}:${pad(m)}:${pad(s)}:${pad(ms, 3)}`;
}

/**
 * Lengths and file paths of a slide's transition, or null when the slide has none (or an unknown
 * one). Resolves to { name, files: [relativePath1, relativePath2], seconds: [len1, len2] }.
 */
export async function getSlideTransition(apiBaseURL, slide, canvasSize) {
  const def = slide && slide.transitionName ? SLIDE_TRANSITIONS[slide.transitionName] : null;
  if (!def || !slide.transitionType || slide.transitionType === "none") return null;
  const folder = slideTransitionFolder(canvasSize);
  const files = [folder + def[0], folder + def[1]];
  const seconds = await Promise.all(
    files.map(async (file, i) => {
      try {
        // Never let a slow server hold up the preview or export: fall back after 8 s.
        const json = await Promise.race([
          loadLottieJson(apiBaseURL, file),
          new Promise((_, reject) => setTimeout(() => reject(new Error("timed out")), 8000)),
        ]);
        const len = animationSeconds(json);
        if (len) return len;
      } catch (e) {
        console.log("Could not read slide transition", file, e);
      }
      return def[2 + i];
    })
  );
  return { name: slide.transitionName, files, seconds };
}

// ---- Lengths without waiting --------------------------------------------------------------------
// The preview sets itself up the moment it opens, from the data that exists at that moment, so it
// must not wait for the network (an earlier version did, and the preview stayed empty).
// preloadSlideTransitions() reads the real lengths in the background (when a project is loaded or
// a transition is chosen); getSlideTransitionNow() answers immediately from what is known.

const knownSeconds = new Map(); // file path -> seconds

/** Reads the real lengths of the transitions used by these slides, in the background. */
export function preloadSlideTransitions(apiBaseURL, slides, canvasSize) {
  if (!Array.isArray(slides)) return Promise.resolve();
  return Promise.all(
    slides.map(async (slide) => {
      const t = await getSlideTransition(apiBaseURL, slide, canvasSize).catch(() => null);
      if (t) t.files.forEach((f, i) => knownSeconds.set(f, t.seconds[i]));
    })
  );
}

/** Like getSlideTransition, but synchronous: real lengths if already read, otherwise the table. */
export function getSlideTransitionNow(slide, canvasSize) {
  const def = slide && slide.transitionName ? SLIDE_TRANSITIONS[slide.transitionName] : null;
  if (!def || !slide.transitionType || slide.transitionType === "none") return null;
  const folder = slideTransitionFolder(canvasSize);
  const files = [folder + def[0], folder + def[1]];
  const seconds = files.map((f, i) => knownSeconds.get(f) || def[2 + i]);
  return { name: slide.transitionName, files, seconds };
}
